import {
    type Geolocation,
    geolocationDistance,
} from '@myphx-engineering/location-utils';

import { getFragment, getFragments } from '../../graphql';
import {
    type ChainProviderInfoFragment,
    type MailOrderProviderInfoFragment,
    type OffersConnectionInfoFragment,
    type PharmacyProviderInfoFragment,
    type ProviderOffersInfoFragment,
} from '../../graphql/generated/graphql';

export function getPrimaryChainProvider(
    provider: ChainProviderInfoFragment,
    geolocation?: Geolocation
): PharmacyProviderInfoFragment | undefined {
    if (provider?.pharmacies?.length) {
        if (!geolocation) {
            return getFragment(provider.pharmacies[0]);
        }
        const getDistance = (pharmacy: PharmacyProviderInfoFragment) =>
            geolocationDistance(geolocation, {
                latitude: pharmacy.latitude,
                longitude: pharmacy.longitude,
            });

        let closestPharmacy: PharmacyProviderInfoFragment | undefined;
        let shortestDistance: number | undefined;

        provider.pharmacies.forEach((pharmacy) => {
            const pharmacyFragment = getFragment(pharmacy);
            const distance = getDistance(pharmacyFragment);
            if (shortestDistance === undefined || distance < shortestDistance) {
                closestPharmacy = pharmacyFragment;
                shortestDistance = distance;
            }
        });
        return closestPharmacy;
    }
    return undefined;
}

export function getProvider(
    providerOffers: ProviderOffersInfoFragment,
    id: string
): MailOrderProviderInfoFragment | PharmacyProviderInfoFragment | undefined {
    if (providerOffers.provider.__typename === 'ChainProvider') {
        const match = getFragment(providerOffers.provider).pharmacies?.find(
            (pharmacy) => getFragment(pharmacy).id === id
        );

        if (match) {
            return getFragment(match);
        }
    }

    return getPrimaryProvider(providerOffers);
}

export function getPrimaryProvider(
    providerOffers:
        | ProviderOffersInfoFragment
        | PharmacyProviderInfoFragment
        | ChainProviderInfoFragment,
    geolocation?: Geolocation
) {
    const provider =
        providerOffers.__typename === 'ProviderOffers'
            ? getFragment(providerOffers.provider)
            : getFragment(providerOffers);

    switch (provider.__typename) {
        case 'ChainProvider':
            return getPrimaryChainProvider(provider, geolocation);
        case 'MailOrderProvider':
        case 'PharmacyProvider':
            return provider;
    }

    return undefined;
}

export function getProviderOffersByType(
    offersConnection: OffersConnectionInfoFragment | undefined,
    featuredOfferId: string = '',
    costPlusDrugsEnabled?: boolean,
    geolocation?: Geolocation
) {
    const pickup =
        getFragments(offersConnection?.providerOffersByType?.pickup)?.filter(
            (entry) => entry.id !== featuredOfferId
        ) ?? [];

    const delivery =
        getFragments(offersConnection?.providerOffersByType?.delivery)?.filter(
            (entry) => entry.id !== featuredOfferId
        ) ?? [];

    if (costPlusDrugsEnabled !== undefined) {
        return {
            pickup: filterProviderOffersByCostPlus(
                pickup,
                costPlusDrugsEnabled,
                geolocation
            ),
            delivery: filterProviderOffersByCostPlus(
                delivery,
                costPlusDrugsEnabled,
                geolocation
            ),
        };
    }

    return {
        pickup,
        delivery,
    };
}

export const hasAdditionalShippingOptions = (
    pharmacyName: string | undefined
): boolean => {
    if (!pharmacyName) {
        return false;
    }

    return pharmacyName.toUpperCase().includes('COST PLUS');
};

/**
 * Filters provider offers to exclude Cost Plus providers when the feature flag is disabled

 */
export function filterProviderOffersByCostPlus(
    providerOffers: ProviderOffersInfoFragment[],
    costPlusDrugsEnabled: boolean,
    geolocation?: Geolocation
): ProviderOffersInfoFragment[] {
    if (costPlusDrugsEnabled) {
        return providerOffers;
    }

    return providerOffers.filter((providerOffer) => {
        const primaryProvider = getPrimaryProvider(providerOffer, geolocation);
        const providerName = primaryProvider?.name;

        return !hasAdditionalShippingOptions(providerName);
    });
}
